import type { ITheme, Terminal } from "@xterm/xterm";
import { TERMINAL_CLOSE, type FsSyncState, type TerminalServerMessage } from "@ide/shared";
import type { KompilDesktopApi } from "@ide/shared/desktop";
import { Emitter } from "@/collab/emitter";

export interface SandboxTicketLike {
  sandboxId: string;
  terminalUrl: string;
  token: string;
  runCommand: string;
}

export type TerminalStatus =
  "idle" | "starting" | "connecting" | "ready" | "reconnecting" | "exited" | "error";

type Events = {
  status: TerminalStatus;
  fs: { state: FsSyncState; pending: number };
  stuck: boolean;
  error: string;
  /** Shell do sandbox (ex.: "bash"), quando o terminal fica pronto. */
  shell: string;
  /** Saída chegando agora (true) ou silêncio há {@link QUIET_MS} (false). */
  activity: boolean;
};

/**
 * Paleta ANSI do terminal. No padrão, tons de cinza: o terminal também segue o preto e branco.
 * Com um tema de cor instalado (classes ui-theme-* no <html>), acompanha as cores da IDE —
 * no Tema Hacker, verde sobre o preto.
 */
export function terminalTheme(dark: boolean): ITheme {
  const root = typeof document === "undefined" ? null : document.documentElement;
  if (root && [...root.classList].some((c) => c.startsWith("ui-theme-"))) return themedTerminal(root);
  const g = dark
    ? ["#101010", "#8c8c8c", "#d0d0d0", "#bdbdbd", "#a8a8a8", "#a3a3a3", "#b5b5b5", "#e6e6e6"]
    : ["#000000", "#3d3d3d", "#2a2a2a", "#4a4a4a", "#555555", "#5c5c5c", "#454545", "#e6e6e6"];
  return {
    background: dark ? "#101010" : "#ffffff",
    foreground: dark ? "#f5f5f5" : "#111111",
    cursor: dark ? "#f5f5f5" : "#000000",
    cursorAccent: dark ? "#101010" : "#ffffff",
    selectionBackground: dark ? "#363636" : "#cfcfcf",
    black: g[0],
    red: g[1],
    green: g[2],
    yellow: g[3],
    blue: g[4],
    magenta: g[5],
    cyan: g[6],
    white: g[7],
    brightBlack: dark ? "#737373" : "#8a8a8a",
    brightRed: dark ? "#b0b0b0" : "#1f1f1f",
    brightGreen: dark ? "#f0f0f0" : "#111111",
    brightYellow: dark ? "#dedede" : "#2b2b2b",
    brightBlue: dark ? "#cacaca" : "#333333",
    brightMagenta: dark ? "#c0c0c0" : "#383838",
    brightCyan: dark ? "#d8d8d8" : "#2e2e2e",
    brightWhite: "#ffffff",
  };
}

/** Terminal nas cores do tema de interface ativo (variáveis de global.css). */
function themedTerminal(root: HTMLElement): ITheme {
  const css = getComputedStyle(root);
  const v = (name: string) => css.getPropertyValue(name).trim();
  const [bg, fg, muted, subtle, emphasis] = [
    "--bg",
    "--fg",
    "--fg-muted",
    "--fg-subtle",
    "--bg-emphasis",
  ].map(v);
  const [red, yellow, blue, magenta, green] = [
    "--code-error",
    "--code-warning",
    "--code-info",
    "--code-hint",
    "--code-add",
  ].map(v);
  return {
    background: bg,
    foreground: fg,
    cursor: fg,
    cursorAccent: bg,
    selectionBackground: emphasis,
    black: subtle,
    red,
    green,
    yellow,
    blue,
    magenta,
    cyan: blue,
    white: muted,
    brightBlack: subtle,
    brightRed: red,
    brightGreen: green,
    brightYellow: yellow,
    brightBlue: blue,
    brightMagenta: magenta,
    brightCyan: magenta,
    brightWhite: fg,
  };
}

const ERROR_RE = /\b(error|erro|exception|traceback|failed|falhou|ERR!|cannot find|not found)\b/i;
const STUCK_WINDOW_MS = 5 * 60_000;
const STUCK_THRESHOLD = 3;
const QUIET_MS = 1_500;

/**
 * O que os dois terminais têm em comum (o do sandbox na nuvem e o local, na máquina): estado,
 * indicador de saída recente e a detecção opt-in de "travado" (roda só aqui, seção 4.3).
 */
abstract class BaseTerminal extends Emitter<Events> {
  abstract readonly kind: "sandbox" | "local";
  protected _status: TerminalStatus = "idle";
  private commandHadError = false;
  private errorTimes: number[] = [];
  private _stuck = false;
  private busy = false;
  private quiet: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected readonly term: Terminal,
    private readonly detectStuck: () => boolean,
  ) {
    super();
  }

  get status() {
    return this._status;
  }

  abstract get runCommand(): string | null;
  abstract connect(): Promise<void>;
  abstract run(command?: string): Promise<boolean>;
  abstract interrupt(): void;
  abstract restart(): Promise<void>;
  abstract flush(timeoutMs?: number): Promise<boolean>;
  abstract disconnect(): void;
  abstract dispose(): void;

  protected setStatus(s: TerminalStatus) {
    this._status = s;
    this.emit("status", s);
  }

  /** Saída do shell: mostra, marca atividade e repara em erros (para o "travado"). */
  protected output(d: string) {
    this.term.write(d);
    if (ERROR_RE.test(d)) this.commandHadError = true;
    this.markOutput();
  }

  /** Indicador de "saída recente" (o processo está escrevendo no terminal agora). */
  private markOutput() {
    if (!this.busy) {
      this.busy = true;
      this.emit("activity", true);
    }
    if (this.quiet) clearTimeout(this.quiet);
    this.quiet = setTimeout(() => {
      this.quiet = null;
      this.busy = false;
      this.emit("activity", false);
    }, QUIET_MS);
  }

  /** Heurística local de "travado": o mesmo fluxo falhando várias vezes em pouco tempo. */
  protected onCommandSubmitted() {
    if (this.commandHadError && this.detectStuck()) {
      const now = Date.now();
      this.errorTimes = [...this.errorTimes.filter((t) => now - t < STUCK_WINDOW_MS), now];
      if (this.errorTimes.length >= STUCK_THRESHOLD && !this._stuck) {
        this._stuck = true;
        this.emit("stuck", true);
      }
    }
    this.commandHadError = false;
  }

  clearStuck() {
    this._stuck = false;
    this.errorTimes = [];
    this.emit("stuck", false);
  }

  protected stopActivity() {
    if (this.quiet) clearTimeout(this.quiet);
  }
}

/**
 * Conexão do terminal do PRÓPRIO sandbox (seção 5.4). Canal individual: nunca
 * passa pelo Yjs nem por conexão compartilhada.
 */
export class TerminalConnection extends BaseTerminal {
  readonly kind = "sandbox" as const;
  private ws: WebSocket | null = null;
  private ticket: SandboxTicketLike | null = null;
  private attempts = 0;
  private stopped = false;
  private disposers: Array<() => void> = [];
  private flushes = new Map<string, (ok: boolean) => void>();

  constructor(
    term: Terminal,
    private readonly ensure: () => Promise<SandboxTicketLike>,
    detectStuck: () => boolean,
    /** Comando do "Executar" quando o projeto não definiu um (lido dos arquivos). */
    private readonly detectRun: () => Promise<string | null> = async () => null,
  ) {
    super(term, detectStuck);
    const data = term.onData((d) => {
      if (d.includes("\r")) this.onCommandSubmitted();
      this.send({ t: "in", d });
    });
    const resize = term.onResize(({ cols, rows }) => this.sendResize(cols, rows));
    this.disposers.push(
      () => data.dispose(),
      () => resize.dispose(),
    );
  }

  get runCommand() {
    return this.ticket?.runCommand ?? null;
  }

  async connect() {
    this.stopped = false;
    this.setStatus(this.attempts === 0 ? "starting" : "reconnecting");
    try {
      // Cria ou acorda o sandbox e recebe um token curto (2 min) só para abrir o WebSocket.
      this.ticket = await this.ensure();
    } catch (err) {
      this.emit("error", err instanceof Error ? err.message : "Não foi possível iniciar o terminal.");
      this.setStatus("error");
      // Sem permissão ou sem o plano da nuvem: tentar de novo não muda a resposta.
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 402 || status === 403 || status === 404) return;
      return this.scheduleReconnect();
    }
    this.setStatus("connecting");
    const url = toWsUrl(this.ticket.terminalUrl);
    const ws = new WebSocket(`${url}?token=${encodeURIComponent(this.ticket.token)}`);
    this.ws = ws;
    ws.onmessage = (e) => this.onMessage(JSON.parse(String(e.data)) as TerminalServerMessage);
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      for (const resolve of this.flushes.values()) resolve(false);
      this.flushes.clear();
      if (this.stopped || this._status === "exited") return;
      if (e.code === TERMINAL_CLOSE.forbidden || e.code === TERMINAL_CLOSE.unauthorized) {
        this.emit("error", "Acesso ao terminal negado.");
        this.setStatus("error");
        return;
      }
      if (e.code === TERMINAL_CLOSE.tooManyTerminals) {
        this.emit(
          "error",
          "Você já está com o máximo de terminais abertos na nuvem. Feche um para abrir outro.",
        );
        this.setStatus("error");
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.stopped) return;
    this.attempts++;
    const delay = Math.min(15_000, 500 * 2 ** Math.min(this.attempts, 5));
    this.setStatus("reconnecting");
    setTimeout(() => void (this.stopped ? null : this.connect()), delay);
  }

  private onMessage(m: TerminalServerMessage) {
    switch (m.t) {
      case "out":
        this.output(m.d);
        break;
      case "ready":
        this.attempts = 0;
        this.setStatus("ready");
        this.emit("shell", m.shell.split(/[\\/]/).pop() || m.shell);
        this.sendResize(this.term.cols, this.term.rows);
        break;
      case "fs":
        this.emit("fs", { state: m.state, pending: m.pending });
        break;
      case "flushed":
        this.flushes.get(m.id)?.(m.ok);
        this.flushes.delete(m.id);
        break;
      case "exit":
        this.term.write(
          `\r\n\x1b[2m[processo encerrado com código ${m.code} — pressione Reiniciar]\x1b[0m\r\n`,
        );
        this.setStatus("exited");
        break;
      case "error":
        this.emit("error", m.message);
        break;
    }
  }

  /**
   * Painel recolhido mede menos de 2 linhas/colunas, o que o protocolo recusa (seção 5.4).
   * Nesse caso o PTY fica com o último tamanho válido até o painel abrir de novo.
   */
  private sendResize(cols: number, rows: number) {
    if (cols < 2 || rows < 2) return;
    this.send({ t: "resize", cols: Math.min(cols, 1000), rows: Math.min(rows, 500) });
  }

  private send(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  /** Espera o disco do sandbox refletir o código compartilhado. */
  flush(timeoutMs = 10_000): Promise<boolean> {
    if (this.ws?.readyState !== WebSocket.OPEN) return Promise.resolve(false);
    const id = Math.random().toString(36).slice(2);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.flushes.delete(id);
        resolve(false);
      }, timeoutMs);
      this.flushes.set(id, (ok) => {
        clearTimeout(timer);
        resolve(ok);
      });
      this.send({ t: "flush", id });
    });
  }

  /** "Executar": salva (flush) e só então digita o comando no shell. */
  async run(command?: string): Promise<boolean> {
    const cmd = command ?? (await this.detectRun()) ?? this.ticket?.runCommand;
    if (!cmd || this._status !== "ready") return false;
    const ok = await this.flush();
    this.send({ t: "in", d: `${cmd}\r` });
    this.onCommandSubmitted();
    return ok;
  }

  interrupt() {
    this.send({ t: "in", d: "\x03" });
  }

  async restart() {
    this.disconnect();
    this.term.reset();
    this.attempts = 0;
    await this.connect();
  }

  disconnect() {
    this.stopped = true;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
  }

  dispose() {
    this.stopActivity();
    this.disconnect();
    this.disposers.forEach((d) => d());
    this.clearListeners();
  }
}

function toWsUrl(url: string): string {
  if (/^wss?:\/\//.test(url)) return url;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${url.startsWith("/") ? url : `/${url}`}`;
}

/**
 * Terminal na MÁQUINA da pessoa (app desktop), como o do VS Code: o shell do sistema (PowerShell,
 * zsh, bash) na pasta da cópia local do projeto. Só a própria pessoa vê; o time vê "rodando".
 */
export class LocalTerminalConnection extends BaseTerminal {
  readonly kind = "local" as const;
  private id: string | null = null;
  private disposers: Array<() => void> = [];
  private detected: string | null = null;
  cwd: string | null = null;

  constructor(
    term: Terminal,
    private readonly api: NonNullable<KompilDesktopApi["terminal"]>,
    private readonly projectId: string,
    detectStuck: () => boolean,
    private readonly detectRun: () => Promise<string | null>,
    /** Grava as edições pendentes na pasta antes de rodar. */
    private readonly beforeRun: () => void,
  ) {
    super(term, detectStuck);
    const data = term.onData((d) => {
      if (d.includes("\r")) this.onCommandSubmitted();
      if (this.id) this.api.write(this.id, d);
    });
    const resize = term.onResize(({ cols, rows }) => {
      if (this.id && cols > 1 && rows > 1) this.api.resize(this.id, cols, rows);
    });
    const out = this.api.onData((e) => {
      if (e.id === this.id) this.output(e.data);
    });
    const exit = this.api.onExit((e) => {
      if (e.id !== this.id) return;
      this.id = null;
      this.term.write(`\r\n\x1b[2m[shell encerrado com código ${e.code} — pressione Reiniciar]\x1b[0m\r\n`);
      this.setStatus("exited");
    });
    this.disposers.push(
      () => data.dispose(),
      () => resize.dispose(),
      out,
      exit,
    );
  }

  get runCommand() {
    return this.detected;
  }

  async connect() {
    this.setStatus("starting");
    const res = await this.api.create(this.projectId, this.term.cols, this.term.rows);
    if (!res.ok) {
      this.emit("error", res.error);
      this.setStatus("error");
      return;
    }
    this.id = res.id;
    this.cwd = res.cwd;
    this.setStatus("ready");
    this.emit("shell", res.shell);
    void this.detectRun().then((c) => (this.detected = c));
  }

  async run(command?: string): Promise<boolean> {
    const cmd = command ?? (await this.detectRun()) ?? this.detected;
    if (!cmd || !this.id || this._status !== "ready") return false;
    this.beforeRun();
    // As últimas edições chegam à pasta antes do comando (a gravação é assíncrona).
    await new Promise((r) => setTimeout(r, 300));
    this.api.write(this.id, `${cmd}\r`);
    this.onCommandSubmitted();
    return true;
  }

  async flush() {
    this.beforeRun();
    await new Promise((r) => setTimeout(r, 300));
    return true;
  }

  interrupt() {
    if (this.id) this.api.write(this.id, "\x03");
  }

  async restart() {
    this.disconnect();
    this.term.reset();
    await this.connect();
  }

  disconnect() {
    if (this.id) this.api.kill(this.id);
    this.id = null;
  }

  dispose() {
    this.stopActivity();
    this.disconnect();
    this.disposers.forEach((d) => d());
    this.clearListeners();
  }
}

export type AnyTerminal = TerminalConnection | LocalTerminalConnection;
